import { z } from 'zod';
import { UrlOrPath } from './url';

/**
 * The board's column catalogue - the Monday column set, in the same five
 * categories the Column Center shows.
 *
 * This file is the single source of truth. The Prisma enum, the API's
 * validation, the Column Center and every cell renderer all read from here,
 * so adding a type is one edit plus a renderer.
 */
export const columnTypes = [
  // Essentials
  'status', 'text', 'person', 'date', 'dropdown', 'number',
  // Super useful
  'checkbox', 'timeline', 'priority', 'rating', 'link', 'last_updated',
  // Communication
  'email', 'phone', 'location', 'country', 'files',
  // Time & tracking
  'time_tracking', 'hour', 'week', 'world_clock',
  // Advanced
  'item_id', 'formula', 'dependency', 'button', 'color_picker', 'tags',
] as const;
export const ColumnType = z.enum(columnTypes);
export type ColumnType = z.infer<typeof ColumnType>;

export const columnCategories = [
  'essentials', 'super_useful', 'communication', 'time_tracking', 'advanced',
] as const;
export type ColumnCategory = (typeof columnCategories)[number];

/**
 * How a column's value comes to exist. The distinction is not cosmetic:
 * `derived` values are computed by the API on read and any client-sent value
 * is discarded, so nothing can write a formula result by hand.
 */
export type ColumnBehaviour = 'stored' | 'derived' | 'action';

export interface ColumnMeta {
  type: ColumnType;
  category: ColumnCategory;
  /** English label; the UI translates via `board.columnTypes.<type>`. */
  title: string;
  description: string;
  /** lucide-react icon name, resolved to a component in the web app. */
  icon: string;
  color: string;
  behaviour: ColumnBehaviour;
  /** Types that carry a `labels` list the user can edit. */
  hasLabels?: boolean;
}

export const COLUMN_META: Record<ColumnType, ColumnMeta> = {
  // ── Essentials ──────────────────────────────────────────────────────────
  status: {
    type: 'status', category: 'essentials', title: 'Status', icon: 'CircleDot',
    color: '#00c875', behaviour: 'stored', hasLabels: true,
    description: 'Get an instant overview of where things stand',
  },
  text: {
    type: 'text', category: 'essentials', title: 'Text', icon: 'Type',
    color: '#ff642e', behaviour: 'stored',
    description: 'Add textual information e.g. addresses, names or keywords',
  },
  person: {
    type: 'person', category: 'essentials', title: 'People', icon: 'Users',
    color: '#579bfc', behaviour: 'stored',
    description: 'Assign people to improve team work',
  },
  date: {
    type: 'date', category: 'essentials', title: 'Date', icon: 'Calendar',
    color: '#00c875', behaviour: 'stored',
    description: 'Add dates like deadlines to ensure you never miss a beat',
  },
  dropdown: {
    type: 'dropdown', category: 'essentials', title: 'Dropdown', icon: 'List',
    color: '#a25ddc', behaviour: 'stored', hasLabels: true,
    description: 'Create a dropdown list of options',
  },
  number: {
    type: 'number', category: 'essentials', title: 'Numbers', icon: 'Hash',
    color: '#fdab3d', behaviour: 'stored',
    description: 'Add revenue, costs, time estimations and more',
  },

  // ── Super useful ────────────────────────────────────────────────────────
  checkbox: {
    type: 'checkbox', category: 'super_useful', title: 'Checkbox', icon: 'SquareCheck',
    color: '#00c875', behaviour: 'stored',
    description: 'Check off items when they are done',
  },
  timeline: {
    type: 'timeline', category: 'super_useful', title: 'Timeline', icon: 'CalendarRange',
    color: '#a25ddc', behaviour: 'stored',
    description: 'Plan work over a start and end date',
  },
  priority: {
    type: 'priority', category: 'super_useful', title: 'Priority', icon: 'Flag',
    color: '#fdab3d', behaviour: 'stored', hasLabels: true,
    description: 'Prioritize tasks and focus on what is most important',
  },
  rating: {
    type: 'rating', category: 'super_useful', title: 'Rating', icon: 'Star',
    color: '#fdab3d', behaviour: 'stored',
    description: 'Rate items on a scale of five stars',
  },
  link: {
    type: 'link', category: 'super_useful', title: 'Link', icon: 'Link',
    color: '#579bfc', behaviour: 'stored',
    description: 'Simply hyperlink to any website',
  },
  last_updated: {
    type: 'last_updated', category: 'super_useful', title: 'Last Updated', icon: 'Clock',
    color: '#c4c4c4', behaviour: 'derived',
    description: 'See who last updated the item and when',
  },

  // ── Communication ───────────────────────────────────────────────────────
  email: {
    type: 'email', category: 'communication', title: 'Email', icon: 'Mail',
    color: '#579bfc', behaviour: 'stored',
    description: 'Email team members and clients directly from your board',
  },
  phone: {
    type: 'phone', category: 'communication', title: 'Phone', icon: 'Phone',
    color: '#00c875', behaviour: 'stored',
    description: 'Call your contacts directly from the board',
  },
  location: {
    type: 'location', category: 'communication', title: 'Location', icon: 'MapPin',
    color: '#e2445c', behaviour: 'stored',
    description: 'Place multiple locations on a geographic map',
  },
  country: {
    type: 'country', category: 'communication', title: 'Country', icon: 'Globe',
    color: '#0086c0', behaviour: 'stored',
    description: 'Choose a country',
  },
  files: {
    type: 'files', category: 'communication', title: 'Files', icon: 'Paperclip',
    color: '#a25ddc', behaviour: 'stored',
    description: 'Add files & docs to your item',
  },

  // ── Time & tracking ─────────────────────────────────────────────────────
  time_tracking: {
    type: 'time_tracking', category: 'time_tracking', title: 'Time tracking', icon: 'Timer',
    color: '#037f4c', behaviour: 'derived',
    description: 'Easily track time spent on each item, group, and board',
  },
  hour: {
    type: 'hour', category: 'time_tracking', title: 'Hour', icon: 'Clock4',
    color: '#fdab3d', behaviour: 'stored',
    description: 'Add times to manage and schedule tasks, shifts and more',
  },
  week: {
    type: 'week', category: 'time_tracking', title: 'Week', icon: 'CalendarDays',
    color: '#579bfc', behaviour: 'stored',
    description: 'Select the week on which each item should be completed',
  },
  world_clock: {
    type: 'world_clock', category: 'time_tracking', title: 'World clock', icon: 'Earth',
    color: '#175a63', behaviour: 'stored',
    description: 'Keep track of the time anywhere in the world',
  },

  // ── Advanced ────────────────────────────────────────────────────────────
  item_id: {
    type: 'item_id', category: 'advanced', title: 'Item ID', icon: 'Fingerprint',
    color: '#9aadbd', behaviour: 'derived',
    description: 'Show a unique ID for each item',
  },
  formula: {
    type: 'formula', category: 'advanced', title: 'Formula', icon: 'Sigma',
    color: '#784bd1', behaviour: 'derived',
    description: 'Use functions to manipulate data across multiple columns',
  },
  dependency: {
    type: 'dependency', category: 'advanced', title: 'Dependency', icon: 'GitBranch',
    color: '#0086c0', behaviour: 'stored',
    description: 'Set up dependencies between items in the board',
  },
  button: {
    type: 'button', category: 'advanced', title: 'Button', icon: 'MousePointerClick',
    color: '#e2445c', behaviour: 'action',
    description: 'Perform actions on items by clicking a button',
  },
  color_picker: {
    type: 'color_picker', category: 'advanced', title: 'Color picker', icon: 'Palette',
    color: '#ff158a', behaviour: 'stored',
    description: 'Manage a design system using a color palette',
  },
  tags: {
    type: 'tags', category: 'advanced', title: 'Tags', icon: 'Tags',
    color: '#66ccff', behaviour: 'stored',
    description: 'Group items across boards with keywords',
  },
};

/** The Column Center's five sections, in display order. */
export const COLUMN_CATEGORY_TITLES: Record<ColumnCategory, string> = {
  essentials: 'Essentials',
  super_useful: 'Super useful',
  communication: 'Communication',
  time_tracking: 'Time & tracking',
  advanced: 'Advanced',
};

export const columnsByCategory = () =>
  columnCategories.map((category) => ({
    category,
    title: COLUMN_CATEGORY_TITLES[category],
    items: columnTypes.map((t) => COLUMN_META[t]).filter((m) => m.category === category),
  }));

// ── value schemas ─────────────────────────────────────────────────────────

const hhmm = /^([01]\d|2[0-3]):[0-5]\d$/;
const hex = /^#[0-9a-fA-F]{6}$/;

/**
 * One schema per column type. This is what replaces the EAV table's typed
 * columns - values land in Item.values (jsonb) and are validated here on
 * the way in, so the database no longer has to carry the type.
 *
 * Derived types still have a schema because the API writes them on read;
 * user input for them is discarded before it reaches here.
 */
export const columnValueSchemas = {
  // Essentials
  status: z.string(),
  text: z.string(),
  person: z.array(z.string().uuid()),
  date: z.string().date().nullable(),
  // Tolerates the single string the column used to store, so boards created
  // before it became multi-select keep working.
  dropdown: z.union([
    z.array(z.string()),
    z.string().transform((v) => (v ? [v] : [])),
  ]),
  number: z.number(),

  // Super useful
  checkbox: z.boolean(),
  timeline: z.object({ from: z.string().date(), to: z.string().date() }).nullable(),
  priority: z.string(),
  rating: z.number().int().min(0).max(5),
  link: z.object({ url: z.string().url(), label: z.string().optional() }).nullable(),
  last_updated: z.object({
    at: z.string().datetime(),
    userId: z.string().uuid().nullable(),
  }).nullable(),

  // Communication
  email: z.string().email().nullable(),
  phone: z.string().regex(/^\+?[0-9 ()-]{4,20}$/).nullable(),
  location: z.object({
    label: z.string().max(200),
    lat: z.number().min(-90).max(90).nullable(),
    lng: z.number().min(-180).max(180).nullable(),
  }).nullable(),
  /** ISO 3166-1 alpha-2. */
  country: z.string().regex(/^[A-Z]{2}$/).nullable(),
  files: z.array(z.object({ url: UrlOrPath, name: z.string(), size: z.number() })),

  // Time & tracking
  time_tracking: z.object({
    seconds: z.number().int().nonnegative(),
    running: z.boolean(),
  }).nullable(),
  hour: z.string().regex(hhmm).nullable(),
  /** ISO week, e.g. `2026-W35`. */
  week: z.string().regex(/^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/).nullable(),
  world_clock: z.string().max(60).nullable(),

  // Advanced
  item_id: z.number().int().positive().nullable(),
  formula: z.union([z.number(), z.string(), z.null()]),
  dependency: z.array(z.string().uuid()),
  button: z.null(),
  color_picker: z.string().regex(hex).nullable(),
  tags: z.array(z.string()),
} satisfies Record<ColumnType, z.ZodTypeAny>;

export const schemaForColumn = (type: ColumnType) => columnValueSchemas[type];

/** Derived and action columns are never written from a client payload. */
export const isWritableColumn = (type: ColumnType) => COLUMN_META[type].behaviour === 'stored';

// ── per-type settings ─────────────────────────────────────────────────────

/** What a Button column does when pressed. */
export const ButtonAction = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('set_status'), columnId: z.string().uuid(), label: z.string() }),
  z.object({ kind: z.literal('move_to_group'), groupId: z.string().uuid() }),
  z.object({ kind: z.literal('assign_me') }),
  z.object({ kind: z.literal('archive') }),
]);
export type ButtonAction = z.infer<typeof ButtonAction>;

export const ColumnSettings = z.object({
  /** formula: the expression, e.g. `{budget} * 1.14`. */
  formula: z.string().max(500).optional(),
  /** number: how the value is rendered. */
  unit: z.enum(['none', 'currency', 'percent']).optional(),
  currency: z.string().length(3).optional(),
  /** button */
  buttonLabel: z.string().max(30).optional(),
  buttonAction: ButtonAction.optional(),
  /** world_clock: IANA zone the column shows, per item value override. */
  defaultTimeZone: z.string().max(60).optional(),
  /** dropdown/status/priority: whether several labels may be picked. */
  multiple: z.boolean().optional(),
}).passthrough();
export type ColumnSettings = z.infer<typeof ColumnSettings>;
